import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FolderOpen, Building2, Package, Paperclip } from 'lucide-react';
import { useBucket, useDeleteBucket } from '../hooks/useBuckets.js';
import { useOrders, useDeleteOrder } from '../hooks/useOrders.js';
import { toast } from '../lib/toast.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import BucketModal from '../components/buckets/BucketModal.js';
import OrderModal from '../components/orders/OrderModal.js';
import type { Order } from '@mayksel/types';
import FileAttachments from '../components/files/FileAttachments.js';
import { useFeatureFlag } from '../lib/posthog.js';

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)',
  won: 'var(--color-brand)',
  lost: 'var(--color-danger)',
  on_hold: 'var(--color-amber)',
  complete: 'var(--color-text-muted)',
};

export default function BucketDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('buckets');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const [editOpen, setEditOpen] = useState(false);

  const filesEnabled = useFeatureFlag('files');
  const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'files'>('overview');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  const { data: bucket, isLoading, error } = useBucket(id ?? '');
  const deleteBucket = useDeleteBucket();
  const { data: ordersData } = useOrders({ bucketId: id ?? '', limit: 100 });
  const deleteOrder = useDeleteOrder();

  const orderLabel = workspace?.orderLabel ?? 'Order';
  const orderLabelPlural = workspace?.orderLabelPlural ?? 'Orders';
  const currency = workspace?.currency ?? 'USD';
  const orders = ordersData?.data ?? [];

  async function handleDeleteOrder(order: Order) {
    if (!confirm(`Delete "${order.name}"? This cannot be undone.`)) return;
    try {
      await deleteOrder.mutateAsync(order.id);
      toast.success(`${orderLabel} deleted`);
    } catch (_err) {
      toast.error(`Failed to delete ${orderLabel}`);
    }
  }

  async function handleDelete() {
    if (!bucket) return;
    if (!confirm(t('detail.delete_confirm', { name: bucket.name }))) return;
    try {
      await deleteBucket.mutateAsync(bucket.id);
      toast.success(t('toast.deleted'));
      navigate('/projects');
    } catch (_err) {
      toast.error(t('toast.delete_error'));
    }
  }

  if (isLoading) {
    return (
      <div className="page-enter">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="skeleton-line" style={{ width: 200, height: 24 }} />
          <div className="skeleton-block" style={{ height: 120, borderRadius: 'var(--radius-lg)' }} />
        </div>
      </div>
    );
  }

  if (error || !bucket) {
    return (
      <div className="page-enter" style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
        <FolderOpen size={48} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-4)' }} />
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
          {tc('errors.not_found')}
        </p>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[bucket.status] ?? 'var(--color-text-secondary)';

  return (
    <div className="page-enter">
      {/* Back + header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <button
          onClick={() => navigate(-1)}
          aria-label={tc('actions.back')}
          style={{
            background: 'none', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', cursor: 'pointer',
            color: 'var(--color-text-secondary)', display: 'flex',
            alignItems: 'center', gap: 'var(--space-1)',
            padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-sm)',
          }}
        >
          <ArrowLeft size={14} /> {tc('actions.back')}
        </button>
        <h1 className="page-title" style={{ flex: 1 }}>{bucket.name}</h1>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={() => setEditOpen(true)}
            style={{
              padding: 'var(--space-2) var(--space-4)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              color: 'var(--color-text)', background: 'var(--color-bg)', cursor: 'pointer',
            }}
          >
            {t('detail.edit_button')}
          </button>
          <button
            onClick={handleDelete}
            style={{
              padding: 'var(--space-2) var(--space-4)', border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              color: 'var(--color-danger)', background: 'var(--color-bg)', cursor: 'pointer',
            }}
          >
            {t('detail.delete_button')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '1px solid var(--color-border)',
        marginBottom: 'var(--space-6)',
      }}>
        {([
          'overview',
          'orders',
          ...(filesEnabled ? ['files' as const] : []),
        ] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 'var(--text-sm)', fontWeight: activeTab === tab ? 'var(--font-medium)' : undefined,
              color: activeTab === tab ? 'var(--color-text)' : 'var(--color-text-secondary)',
              borderBottom: activeTab === tab ? '2px solid var(--color-brand)' : '2px solid transparent',
              marginBottom: -1,
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            }}
          >
            {tab === 'orders' ? (
              <>
                {orderLabelPlural}
                {orders.length > 0 && (
                  <span style={{
                    fontSize: 'var(--text-xs)', background: 'var(--color-bg-subtle)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-full)', padding: '0 6px', color: 'var(--color-text-secondary)',
                  }}>
                    {orders.length}
                  </span>
                )}
              </>
            ) : tab === 'files' ? (
              <><Paperclip size={14} />{tc('files:tab', 'Files')}</>
            ) : (
              tc(`tabs.${tab}`, tab.charAt(0).toUpperCase() + tab.slice(1))
            )}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <>

      {/* Summary card */}
      <div style={{
        background: 'var(--color-bg)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)',
        marginBottom: 'var(--space-6)',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 'var(--space-5)',
      }}>
        {/* Status */}
        <div>
          <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.status')}</p>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
            color: statusColor,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
            {t(`status.${bucket.status}`)}
          </span>
        </div>

        {/* Company */}
        {bucket.companyName && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.company')}</p>
            <button
              onClick={() => navigate(`/companies/${bucket.companyId}`)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontSize: 'var(--text-sm)', color: 'var(--color-brand)', textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
              }}
            >
              <Building2 size={14} />
              {bucket.companyName}
            </button>
          </div>
        )}

        {/* Value */}
        {bucket.value && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.value')}</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
              {currency} {parseFloat(bucket.value).toLocaleString()}
            </p>
          </div>
        )}

        {/* Probability */}
        {bucket.probability != null && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('form.probability_label')}</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{bucket.probability}%</p>
          </div>
        )}

        {/* Expected close */}
        {bucket.expectedClose && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.expected_close')}</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{bucket.expectedClose}</p>
          </div>
        )}
      </div>

      {/* Partner companies */}
      {(bucket.partners?.length ?? 0) > 0 && (
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
          marginBottom: 'var(--space-5)',
        }}>
          <p className="label" style={{ marginBottom: 'var(--space-3)' }}>{t('detail.partners_section')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {bucket.partners!.map((partner) => (
              <div key={partner.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <Building2 size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                <button
                  onClick={() => navigate(`/companies/${partner.companyId}`)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontSize: 'var(--text-sm)', color: 'var(--color-brand)',
                  }}
                >
                  {partner.companyName}
                </button>
                {partner.role && (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    · {partner.role}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {bucket.notes && (
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
        }}>
          <p className="label" style={{ marginBottom: 'var(--space-3)' }}>{t('form.notes_label')}</p>
          <p style={{
            fontSize: 'var(--text-sm)', color: 'var(--color-text)',
            lineHeight: 'var(--leading-relaxed)', whiteSpace: 'pre-wrap',
          }}>
            {bucket.notes}
          </p>
        </div>
      )}

      </>}

      {activeTab === 'orders' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <button
              onClick={() => { setEditingOrder(null); setOrderModalOpen(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-4)', background: 'var(--color-brand)',
                color: 'var(--color-text-inverse)', border: 'none',
                borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)', cursor: 'pointer',
              }}
            >
              <Package size={14} />
              {`New ${orderLabel}`}
            </button>
          </div>

          {orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
              <Package size={40} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-3)' }} />
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                {`No ${orderLabelPlural} yet`}
              </p>
            </div>
          ) : (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              {orders.map((order, idx) => (
                <div
                  key={order.id}
                  style={{
                    display: 'grid', gridTemplateColumns: '110px 1fr 130px 90px auto',
                    gap: 'var(--space-4)', padding: 'var(--space-3) var(--space-4)',
                    borderBottom: idx < orders.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                    alignItems: 'center',
                  }}
                >
                  <button
                    onClick={() => navigate(`/orders/${order.id}`)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      textAlign: 'left', fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono, monospace)',
                    }}
                  >
                    {order.orderNumber}
                  </button>
                  <button
                    onClick={() => navigate(`/orders/${order.id}`)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      textAlign: 'left', fontSize: 'var(--text-sm)',
                      color: 'var(--color-text)', fontWeight: 'var(--font-medium)',
                    }}
                  >
                    {order.name}
                  </button>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    {order.stageName ?? order.status}
                  </span>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    {order.total !== '0' ? `${currency} ${parseFloat(order.total).toLocaleString()}` : '—'}
                  </span>
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button
                      onClick={() => { setEditingOrder(order); setOrderModalOpen(true); }}
                      style={{
                        background: 'none', border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-sm)', padding: '2px var(--space-2)',
                        fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => void handleDeleteOrder(order)}
                      style={{
                        background: 'none', border: '1px solid var(--color-danger)',
                        borderRadius: 'var(--radius-sm)', padding: '2px var(--space-2)',
                        fontSize: 'var(--text-xs)', color: 'var(--color-danger)', cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'files' && id && (
        <FileAttachments entityType="bucket" entityId={id} />
      )}

      {editOpen && (
        <BucketModal
          bucket={bucket}
          onClose={() => setEditOpen(false)}
        />
      )}

      {orderModalOpen && (
        <OrderModal
          order={editingOrder}
          defaultBucketId={bucket.id}
          defaultBucketName={bucket.name}
          onClose={() => { setOrderModalOpen(false); setEditingOrder(null); }}
        />
      )}
    </div>
  );
}
